import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Tabs, Tab, CircularProgress, Alert } from "@mui/material";
import { fetchCustomer } from "./api";
import CustomerHeader from "./components/CustomerHeader";
import OverviewTab from "./components/OverviewTab";
import OrdersTab from "./components/OrdersTab";
import UnlocksTab from "./components/UnlocksTab";
import CreditsTab from "./components/CreditsTab";
import CreditPurchasesTab from "./components/CreditPurchasesTab";
import ProductPurchasesTab from "./components/ProductPurchasesTab";
import CreditAdjustDialog from "./components/CreditAdjustDialog";
import ClarityPopup from "../superAdminPages/registerUserlist/ClarityPopup";

const TAB_VALUES = [
  "overview",
  "orders",
  "unlocks",
  "credits",
  "credit-purchases",
  "product-purchases",
];

const normalizeTab = (tab) => {
  if (tab === "utm") return "overview";
  return TAB_VALUES.includes(tab) ? tab : "overview";
};

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clarityOpen, setClarityOpen] = useState(false);
  const [creditOp, setCreditOp] = useState(null);
  const [creditsRefresh, setCreditsRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetchCustomer(id);
        if (!cancelled) setProfile(res.data.data);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.message || "Failed to load customer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleTab = (_, value) => {
    setSearchParams(value === "overview" ? {} : { tab: value });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Customer not found"}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: { xs: 2, md: 3 }, maxWidth: { xs: "100%", md: 1200, lg: 1600 } }}
    >
      <CustomerHeader
        user={profile.user}
        wallet={profile.wallet}
        onBack={() => navigate("/dashboard/customers")}
        onClarity={() => setClarityOpen(true)}
        onAssignCredits={() => setCreditOp("assign")}
        onDeductCredits={() => setCreditOp("deduct")}
      />

      <Tabs
        value={tab}
        onChange={handleTab}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 2.5,
          minHeight: 40,
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: 40,
            color: "#6b7280",
            fontWeight: 500,
          },
          "& .Mui-selected": { color: "#2563eb" },
          "& .MuiTabs-indicator": { backgroundColor: "#2563eb" },
        }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="orders" label={`Orders (${profile.counts?.orders ?? 0})`} />
        <Tab
          value="unlocks"
          label={`Unlocks (${profile.counts?.unlocks ?? 0})`}
        />
        <Tab value="credits" label="Credit history" />
        <Tab
          value="credit-purchases"
          label={`Credit purchases (${profile.counts?.creditPurchases ?? 0})`}
        />
        <Tab
          value="product-purchases"
          label={`Product purchases (${profile.counts?.productPurchases ?? 0})`}
        />
      </Tabs>

      {tab === "overview" && <OverviewTab profile={profile} />}
      {tab === "orders" && <OrdersTab customerId={id} />}
      {tab === "unlocks" && <UnlocksTab customerId={id} />}
      {tab === "credits" && <CreditsTab customerId={id} refreshKey={creditsRefresh} />}
      {tab === "credit-purchases" && <CreditPurchasesTab customerId={id} />}
      {tab === "product-purchases" && <ProductPurchasesTab customerId={id} />}

      <ClarityPopup
        open={clarityOpen}
        onClose={() => setClarityOpen(false)}
        email={profile.user?.email}
      />
      <CreditAdjustDialog
        open={Boolean(creditOp)}
        operation={creditOp}
        customerId={id}
        remaining={profile.wallet?.remaining_credits}
        onClose={() => setCreditOp(null)}
        onSuccess={(wallet) => {
          setProfile((prev) => (prev ? { ...prev, wallet } : prev));
          setCreditsRefresh((n) => n + 1);
        }}
      />
    </Box>
  );
};

export default CustomerDetailPage;
